import jwtDecode from 'jwt-decode';
import OidcService from '../services/oidc.service';
import AuthModule from './AuthModule';

/**
 * Sisi frontend login OIDC fe-web-checklist.
 *
 * CATATAN SENGAJA TENTANG DUPLIKASI: `terapkanSesi` menyimpan sesi ke
 * localStorage dengan cara yang sama seperti jalur SSO lama di `App/App.js`.
 * Jalur lama itu SENGAJA tidak disentuh -- ia dipakai seluruh pengguna checklist
 * setiap hari, dan menyatukan keduanya sekarang berarti mempertaruhkan login
 * semua orang demi kerapian. Begitu OIDC jadi satu-satunya jalur, blok lama itu
 * DIHAPUS -- bukan digabungkan -- dan duplikasi ini hilang sendirinya.
 *
 * 🔴 Bentuk sesi BERBEDA antar-aplikasi LNK. Di fe-web-checklist `roles` adalah
 * `role_name` peran default (dengan cadangan `guest user` bila tidak ada peran
 * default), dan `selected_role` dirakit dari `org_name_short` + `position_name`
 * -- BUKAN `role_name` seperti fe-visit. Menyalin versi aplikasi lain apa
 * adanya membuat menu atau label peran salah secara halus, bukan galat.
 *
 * Akun guest dan aplikasi mobile tidak pernah lewat sini: pengalihan ke OP hanya
 * terjadi di cabang yang sudah membuktikan cookie SSO yang sah.
 */

const aktif = () => OidcService.aktif();

/** Mulai login: navigasi penuh ke be-checklist, yang meneruskan ke OP. */
const urlLoginUntukHalamanIni = () => {
	const kembaliKe = `${window.location.origin}${window.location.pathname}`;
	return OidcService.urlLogin(kembaliKe);
};

/** Tiket yang ditempelkan callback be-checklist di URL, bila ada. */
const ambilTiketDariUrl = (search) => {
	const params = new URLSearchParams(
		typeof search === 'string' ? search : window.location.search,
	);
	return params.get(OidcService.PARAM_TIKET);
};

/**
 * Buang parameter tiket dari address bar tanpa memuat ulang halaman. Tiket
 * memang sudah tidak berlaku setelah ditebus, tapi membiarkannya di URL membuat
 * pengguna bisa mem-bookmark atau membagikan tautan yang menyesatkan -- dan
 * tautan itu akan gagal, karena tiketnya sekali pakai.
 */
const bersihkanTiketDariUrl = () => {
	const url = new URL(window.location.href);
	if (!url.searchParams.has(OidcService.PARAM_TIKET)) return null;
	url.searchParams.delete(OidcService.PARAM_TIKET);
	const bersih = `${url.pathname}${url.search}${url.hash}`;
	window.history.replaceState({}, '', bersih);
	return bersih;
};

/**
 * Pesan galat yang ditempelkan callback be-checklist saat login OIDC gagal, bila ada.
 *
 * Callback itu didatangi NAVIGASI BROWSER, jadi dulu kegagalan dijawab JSON dan
 * pengguna melihat `{"error":true,"message":...}` polos di layar kosong.
 * Sekarang callback memulangkan pengguna ke sini dengan `?oidc_error=`, dan
 * aplikasilah yang menampilkannya sebagai dialog.
 *
 * Yang menentukan adalah KEHADIRAN parameternya, bukan isinya. Parameter yang
 * ada tapi kosong tetap berarti "login gagal" dan dijawab pesan umum yang sama
 * dengan fallback be-checklist. Kalau dianggap tidak ada, alurnya mengalihkan ke OP
 * lagi -- dan repo ini tidak punya pagar waktu yang bisa menahan lingkarannya.
 */
const PESAN_GALAT_UMUM = 'Login OIDC gagal';

const ambilGalatDariUrl = (search) => {
	const params = new URLSearchParams(
		typeof search === 'string' ? search : window.location.search,
	);
	if (!params.has(OidcService.PARAM_GALAT)) return null;
	const pesan = (params.get(OidcService.PARAM_GALAT) || '').trim();
	return pesan || PESAN_GALAT_UMUM;
};

/**
 * Buang parameter galat dari address bar tanpa memuat ulang halaman.
 *
 * Wajib dilakukan begitu galatnya dibaca: kalau dibiarkan, memuat ulang halaman
 * memunculkan dialog yang sama lagi, dan tautan yang ter-bookmark membawa pesan
 * galat yang sudah tidak relevan.
 */
const bersihkanGalatDariUrl = () => {
	const url = new URL(window.location.href);
	if (!url.searchParams.has(OidcService.PARAM_GALAT)) return null;
	url.searchParams.delete(OidcService.PARAM_GALAT);
	const bersih = `${url.pathname}${url.search}${url.hash}`;
	window.history.replaceState({}, '', bersih);
	return bersih;
};

const tebusTiket = (ticket) => OidcService.redeem(ticket);

/**
 * OIDC hanya boleh dimulai dari jendela TERATAS.
 *
 * Bila halaman ini kebetulan dimuat di dalam frame (mis. WebView/iframe pihak
 * lain), OIDC tidak boleh dimulai dari situ. OpenID Provider One LNK mengirim
 * `x-frame-options: SAMEORIGIN` dan CSP `frame-ancestors 'self'` -- diperiksa
 * langsung pada halaman `interaction`-nya -- jadi halaman loginnya TIDAK akan
 * dirender di dalam frame milik domain lain. Tanpa pagar ini, pengguna yang
 * memakai fe-web-checklist dalam mode embed hanya melihat frame kosong, tanpa galat.
 *
 * Di dalam frame kita jatuh kembali ke jalur login lama, yang tetap bekerja.
 */
const bisaDiJendelaIni = () => {
	try {
		return window.self === window.top;
	} catch (e) {
		// Membaca window.top dari frame lintas-origin bisa melempar; kejadian itu
		// sendiri sudah berarti kita berada di dalam frame.
		return false;
	}
};

/**
 * Simpan sesi hasil penebusan tiket. Mengembalikan true bila sesinya dipakai.
 *
 * Mengembalikan false (dan TIDAK menyimpan apa pun) bila sesinya tidak layak:
 * tanpa `accessToken`, atau tanpa `current_person` di dalamnya. Syarat kedua
 * ikut dari jalur lama -- di sana `roles`, `position`, dan menu hanya diisi
 * DI DALAM `if (current_person)`.
 *
 * Isi kuncinya sama dengan login ulang berbekal cookie SSO di `App/App.js`.
 * Satu beda yang disengaja: jalur lama menulis `accessToken` dan `list_roles`
 * SEBELUM memeriksa `current_person` dan sebelum `getMenu`, sehingga kegagalan
 * di tengah meninggalkan sesi setengah jadi. Di sini `getMenu` dipanggil LEBIH
 * DULU dan localStorage baru ditulis kalau ia berhasil. Aman karena `getMenu`
 * (`authorization/<peran>`) hanya butuh token aplikasi, bukan token pengguna.
 */
const terapkanSesi = async (sesi) => {
	if (!sesi?.accessToken) return false;

	const isiToken = jwtDecode(sesi.accessToken);
	if (!isiToken?.details?.hris_org_tree?.current_person) return false;

	// Cadangan `guest user` sama dengan jalur lama: tanpa peran default, menu
	// yang dipakai adalah menu guest.
	const peranDefault = [...(sesi.role || [])].find((p) => p?.is_default) || {
		role_name: 'guest user',
	};

	await AuthModule.getMenu(peranDefault.role_name);

	localStorage.setItem('accessToken', JSON.stringify(sesi));
	localStorage.setItem('list_roles', JSON.stringify(sesi?.role));
	localStorage.setItem('roles', JSON.stringify(peranDefault.role_name));
	localStorage.setItem(
		'selected_role',
		JSON.stringify(`${peranDefault.org_name_short} ${peranDefault.position_name}`),
	);
	localStorage.setItem('position', JSON.stringify({ ...peranDefault }));
	localStorage.setItem('photo', JSON.stringify({ ...sesi?.photo }));
	return true;
};

/**
 * Apakah sesi yang sedang berjalan lahir dari OIDC?
 *
 * Penandanya `oidc_sid` di dalam token — hanya sesi yang diterbitkan lewat OP
 * yang punya itu. Sesi jalur lama tidak, dan perilakunya tidak boleh berubah.
 */
const sesiDariOidc = () => {
	try {
		const simpanan = JSON.parse(localStorage.getItem('accessToken'));
		if (!simpanan?.accessToken) return false;
		return Boolean(jwtDecode(simpanan.accessToken)?.oidc_sid);
	} catch (e) {
		// Penyimpanan rusak/kosong atau token bukan JWT. Jatuh ke jalur logout
		// lama, bukan melempar di dalam penangan klik tombol logout.
		return false;
	}
};

/**
 * Ke mana logout harus menuju: URL `end_session` bila sesi ini memang lahir
 * dari OIDC, atau null supaya pemanggil memakai jalur logout lamanya.
 *
 * Dipisah dari komponen supaya keputusannya bisa diuji tanpa merender apa pun.
 */
const tujuanLogout = () => (aktif() && sesiDariOidc() ? OidcService.urlLogout() : null);

export default {
	aktif,
	urlLoginUntukHalamanIni,
	ambilTiketDariUrl,
	bersihkanTiketDariUrl,
	ambilGalatDariUrl,
	bersihkanGalatDariUrl,
	tebusTiket,
	terapkanSesi,
	bisaDiJendelaIni,
	sesiDariOidc,
	tujuanLogout,
};
